import { useState, useRef } from 'react';
import { Upload, X, Check, Image as ImageIcon, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '../ui/Modal';
import type { MediaCategory, MediaUploadPayload } from './MediaConfig';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: MediaUploadPayload) => Promise<void>;
  categories: MediaCategory[];
  maxFileSizeMb?: number;
}

/**
 * Comprime imagem no navegador via HTML5 Canvas (Max 1200px, 0.78 qualidade).
 * Reduz arquivos de 4MB para ~180KB-250KB antes de enviar ao servidor.
 */
function compressImage(file: File): Promise<{ dataUrl: string; originalSize: number; compressedSize: number }> {
  return new Promise((resolve, reject) => {
    const originalSize = file.size;
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const MAX_WIDTH = 1200;
        let width = img.width;
        let height = img.height;

        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve({ dataUrl: e.target?.result as string, originalSize, compressedSize: originalSize });
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.78);
        const compressedSize = Math.round((dataUrl.length * 3) / 4);

        resolve({ dataUrl, originalSize, compressedSize });
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export function MediaUploadModal({
  open,
  onClose,
  onSubmit,
  categories,
  maxFileSizeMb = 10,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState(categories[0]?.id || 'OUTRO');
  const [preview, setPreview] = useState('');
  const [tipoArquivo, setTipoArquivo] = useState<'IMAGEM' | 'PDF'>('IMAGEM');
  const [compressStats, setCompressStats] = useState<{ original: number; compressed: number } | null>(null);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setTitulo('');
    setDescricao('');
    setCategoria(categories[0]?.id || 'OUTRO');
    setPreview('');
    setTipoArquivo('IMAGEM');
    setCompressStats(null);
    setSaving(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > maxFileSizeMb * 1024 * 1024) {
      toast.error(`O arquivo excede o limite máximo de ${maxFileSizeMb}MB.`);
      return;
    }

    if (file.type.startsWith('image/')) {
      try {
        const res = await compressImage(file);
        setPreview(res.dataUrl);
        setTipoArquivo('IMAGEM');
        setCompressStats({ original: res.originalSize, compressed: res.compressedSize });
        if (!titulo) {
          const baseName = file.name.replace(/\.[^/.]+$/, '');
          setTitulo(baseName);
        }
      } catch {
        toast.error('Erro ao processar imagem.');
      }
    } else if (file.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onload = () => {
        setPreview(reader.result as string);
        setTipoArquivo('PDF');
        setCompressStats(null);
        if (!titulo) {
          const baseName = file.name.replace(/\.[^/.]+$/, '');
          setTitulo(baseName);
        }
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Selecione uma imagem (JPG, PNG, WebP) ou documento PDF.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo.trim()) {
      toast.error('Preencha o título da mídia.');
      return;
    }
    if (!preview) {
      toast.error('Selecione uma imagem ou arquivo.');
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        titulo: titulo.trim(),
        descricao: descricao.trim() || undefined,
        categoria,
        arquivoUrl: preview,
        tipoArquivo,
      });
      toast.success('Mídia adicionada ao banco com sucesso!');
      handleClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Falha ao salvar mídia.';
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setSaving(false);
    }
  };

  const fmtKb = (bytes: number) => `${Math.round(bytes / 1024)} KB`;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Adicionar Mídia ao Banco"
      size="md"
      footer={
        <>
          <button type="button" className="btn-cancel" onClick={handleClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="btn-save" onClick={handleSubmit} disabled={saving || !preview || !titulo.trim()}>
            {saving ? 'Salvando...' : 'Salvar Mídia'}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="mdc-upload-form">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png, image/jpeg, image/webp, application/pdf"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <div className="mdc-dropzone" onClick={() => fileInputRef.current?.click()}>
          {preview ? (
            tipoArquivo === 'IMAGEM' ? (
              <img src={preview} alt="Prévia" className="mdc-dropzone-img" />
            ) : (
              <div style={{ padding: 20, textAlign: 'center' }}>
                <ImageIcon size={36} color="#2563eb" />
                <p style={{ margin: '8px 0 0', fontWeight: 700, fontSize: '.84rem' }}>Documento PDF Selecionado</p>
                <small style={{ color: 'var(--text-muted)' }}>Clique para trocar o arquivo</small>
              </div>
            )
          ) : (
            <>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
                <Upload size={22} />
              </div>
              <strong style={{ fontSize: '.84rem', color: 'var(--text-main)' }}>Clique para selecionar imagem ou PDF</strong>
              <small style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>JPG, PNG, WebP ou PDF (otimização automática)</small>
            </>
          )}
        </div>

        {compressStats && (
          <div className="mdc-compress-badge">
            <Sparkles size={13} />
            <span>Otimizado: {fmtKb(compressStats.original)} → <strong>{fmtKb(compressStats.compressed)}</strong> ({Math.round(((compressStats.original - compressStats.compressed) / compressStats.original) * 100)}% mais leve)</span>
          </div>
        )}

        <div className="mdc-form-field">
          <label>Título da Mídia *</label>
          <input
            type="text"
            className="mdc-form-input"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex: Folder - Reunião de Pais e Mestres"
            required
          />
        </div>

        <div className="mdc-form-field">
          <label>Categoria</label>
          <select
            className="mdc-form-select"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mdc-form-field">
          <label>Descrição ou Observação (opcional)</label>
          <textarea
            className="mdc-form-textarea"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Informações adicionais sobre esta mídia..."
          />
        </div>
      </form>
    </Modal>
  );
}
